import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/SystemConfigContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import {
  Plane, LayoutDashboard, Calendar, Users, ClipboardCheck,
  Shield, DollarSign, FileText, Settings, LogOut, Menu, X,
  ChevronDown, User as UserIcon, ChevronLeft, ChevronRight, FileSignature, Wallet, Video, BarChart3, Upload, Package, Megaphone,
  ArrowLeftRight, CreditCard, Phone, MessageSquare, CalendarDays, ShoppingBag, Gift, Building2, Check,
  Loader2, UserCog,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { DROPZONES, hasSwitcher, currentDropzone, switchDropzone, isGroupHost } from '../utils/dropzones.js';
import { useSsoStatus, useSsoHandoff } from '../hooks/useDropzoneSso';

const AppLayout = () => {
  const { user, logout, hasPermission, activeView, switchView, canSwitchView } = useAuth();
  // Switching sends you to another field's own host, where you sign in with
  // that field's own account and act with its own rights. It is a move between
  // deployments, not a view preference -- owner/admin only.
  const activeDropzone = currentDropzone();
  const canSwitchDropzone = hasSwitcher() && (user?.role === 'owner' || user?.role === 'admin');

  // ── Hopping to another field without typing a password again ────────────
  // Any host in the group can mint a one-shot, 60-second sign-in link for
  // another. It is only ever an optimisation: anything that goes wrong — SSO
  // not configured, no account at that field, an admin rather than an owner —
  // and we fall through to plain navigation, which lands on that field's login
  // page. The user is never stranded, and never told about machinery.
  const ssoStatus = useSsoStatus({ enabled: canSwitchDropzone });
  const handoff = useSsoHandoff();
  const [hoppingTo, setHoppingTo] = React.useState(null);

  const goToDropzone = async (slug) => {
    if (!ssoStatus.data?.canHandoff) return switchDropzone(slug);
    setHoppingTo(slug);
    try {
      const { url } = await handoff.mutateAsync(slug);
      window.location.assign(url);
    } catch {
      setHoppingTo(null);
      switchDropzone(slug);
    }
  };
  const { config } = useConfig();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState('');
  const [businessDate, setBusinessDate] = useState('');

  React.useEffect(() => {
    const tz = config?.general?.timezone || 'UTC';
    const is24h = config?.general?.timeFormat === '24h';
    const tzOption = { timeZone: tz };

    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString(is24h ? 'en-GB' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: !is24h,
        ...tzOption,
      });
      const dateString = now.toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', ...tzOption,
      });
      setCurrentTime(timeString);
      setBusinessDate(dateString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [config?.general?.timeFormat, config?.general?.timezone]);

  React.useEffect(() => {
    document.title = 'UltimateDZ Management';
  }, []);

  const handleLogout = () => {
    // Route the post-logout login page by whether the user had a staff profile
    // (source of truth for staff identity) rather than the legacy user.role.
    // Covers dual-profile users — they land on the staff login since they
    // had staff access.
    const wasStaff = !!user?.staffProfile || !!user?.hasStaffProfile;
    logout();
    navigate(wasStaff ? '/login?type=staff' : '/login');
  };

  // Navigation Items Definition
  const allNavItems = {
    dashboard: { name: t('sidebar.dashboard'), href: '/app/dashboard', icon: LayoutDashboard, permission: 'dashboard' },
    analytics: { name: t('sidebar.analytics'), href: '/app/analytics', icon: BarChart3, permission: 'dashboard' },
    manifest: { name: t('sidebar.manifest'), href: '/app/manifest', icon: Calendar, permission: 'manifest' },
    staff: { name: t('sidebar.staff'), href: '/app/staff', icon: UserIcon, permission: 'staff' },
    customers: { name: t('sidebar.customers'), href: '/app/customers', icon: Users, permission: 'customers' },
    documents: { name: t('sidebar.documents'), href: '/app/documents', icon: FileSignature, permission: 'documents' },
    checkin: { name: t('sidebar.checkIn'), href: '/app/check-in', icon: ClipboardCheck, permission: 'check-in' },
    aircraft: { name: t('sidebar.aircraft'), href: '/app/aircraft', icon: Plane, permission: 'aircraft' },
    safety: { name: t('sidebar.safety'), href: '/app/safety', icon: Shield, permission: 'safety' },
    finance: { name: t('sidebar.finance'), href: '/app/finance', icon: DollarSign, permission: 'finance' },
    reports: { name: t('sidebar.reports'), href: '/app/reports', icon: FileText, permission: 'reports' },
    admin: { name: t('sidebar.admin'), href: '/app/admin', icon: Settings, permission: 'admin' },
    // Super Admin console (group host, owner only). Its sections are the
    // sidebar on that host — one item per section, no wrapper item.
    groupOverview: { name: t('sidebar.groupOverview'), href: '/app/group', icon: LayoutDashboard, permission: 'admin' },
    groupCustomers: { name: t('sidebar.groupCustomers'), href: '/app/group/customers', icon: Users, permission: 'admin' },
    groupStaff: { name: t('sidebar.groupStaff'), href: '/app/group/staff', icon: UserCog, permission: 'admin' },
    groupBookings: { name: t('sidebar.groupBookings'), href: '/app/group/bookings', icon: CalendarDays, permission: 'admin' },
    groupTransfers: { name: t('sidebar.groupTransfers'), href: '/app/group/transfers', icon: ArrowLeftRight, permission: 'admin' },
    calls: { name: t('sidebar.voiceAgent'), href: '/app/voice-agent', icon: Phone, permission: 'voice-agent' },
    calendarConfig: { name: t('sidebar.calendarConfig'), href: '/app/calendar-config', icon: Calendar, permission: 'admin' },
    smsConfig: { name: t('sidebar.smsConfig'), href: '/app/sms-config', icon: MessageSquare, permission: 'admin' },
    announcements: { name: t('sidebar.announcements'), href: '/app/announcements', icon: Megaphone, permission: 'manifest' },
    profile: { name: t('sidebar.profile'), href: '/app/profile', icon: UserIcon, permission: 'profile' },
    // Staff-facing weekly/day booking planner. Defined BEFORE `bookings` so
    // the permission→navKey map (used by the effectiveModules override path)
    // resolves the 'bookings' module to THIS staff calendar rather than the
    // customer-only "My Bookings" list — customers still get "My Bookings"
    // because the customer view uses the hardcoded customer nav, not the map.
    bookingsSchedule: { name: t('sidebar.bookingsSchedule'), href: '/app/bookings/schedule', icon: CalendarDays, permission: 'bookings' },
    bookings: { name: t('sidebar.myBookings'), href: '/app/bookings', icon: Calendar, permission: 'bookings', customerOnly: true },
    wallet: { name: t('sidebar.myWallet'), href: '/app/wallet', icon: Wallet, permission: 'wallet' },
    // Dropzone shop (Items). customerOnly so it stays out of the staff lane —
    // staff sell/fulfil items from the customer profile page.
    shop: { name: t('sidebar.shop'), href: '/app/shop', icon: ShoppingBag, permission: 'items', customerOnly: true },
    schedule: { name: t('sidebar.mySchedule'), href: '/app/schedule', icon: Calendar, permission: 'schedule' },
    media: { name: t('sidebar.media'), href: '/app/media', icon: Video, permission: 'media' },
    myUploads: { name: t('sidebar.myUploads'), href: '/app/my-uploads', icon: Upload, permission: 'media' },
    myMedia: { name: t('sidebar.myMedia'), href: '/app/my-media', icon: Video, permission: 'my-media' },
    packing: { name: t('sidebar.packing'), href: '/app/packing', icon: Package, permission: 'packing' },
    giftCards: { name: t('sidebar.giftCards'), href: '/app/gift-cards', icon: CreditCard, permission: 'gift-cards' },
    // Customer-facing gift cards (buy + email to someone). customerOnly keeps
    // it out of the staff lane; staff manage cards via `giftCards` above.
    myGiftCards: { name: t('sidebar.myGiftCards'), href: '/app/my-gift-cards', icon: Gift, permission: 'my-gift-cards', customerOnly: true },
    funJumper: { name: t('sidebar.funJumper'), href: '/app/fun-jumper', icon: Plane, permission: 'fun-jumper' },
  };

  // Role-based Navigation Order
  const getRoleNavigation = (role) => {
    switch(role) {
      case 'admin':
      case 'owner':
        return ['dashboard','analytics', 'manifest', 'bookingsSchedule', 'staff', 'customers', 'documents', 'checkin', 'aircraft', 'safety', 'media', 'packing', 'giftCards', 'finance', 'reports', 'calls', 'admin', 'groupOverview', 'groupCustomers', 'groupStaff', 'groupBookings', 'groupTransfers', 'calendarConfig', 'smsConfig', 'announcements', 'profile'];
      case 'customer':
        return ['dashboard', 'funJumper', 'bookings', 'wallet', 'shop', 'myGiftCards', 'myMedia', 'checkin', 'profile'];
      case 'manifest':
        return ['dashboard', 'manifest', 'bookingsSchedule', 'customers', 'documents', 'media', 'aircraft', 'giftCards', 'calls', 'announcements', 'profile'];
      case 'instructor':
      case 'tandem-instructor':
      case 'aff-instructor':
      case 'wingsuit-instructor':
      case 'coach':
        return ['dashboard', 'schedule', 'myUploads', 'customers', 'media', 'safety', 'profile'];
      case 'pilot':
      case 'chief_pilot':
        return ['dashboard', 'manifest', 'aircraft', 'schedule', 'profile'];
      case 'safety':
        return ['dashboard', 'manifest', 'safety', 'media', 'customers', 'documents', 'profile'];
      case 'finance':
        return ['dashboard', 'finance', 'giftCards', 'customers', 'reports', 'profile'];
      case 'packer':
        return ['dashboard', 'packing', 'profile'];
      case 'rigger':
        return ['dashboard', 'packing', 'safety', 'documents', 'profile'];
      default:
        // Default fallback for any other roles
        return ['dashboard', 'profile'];
    }
  };

  // ─── Build navigation based on activeView ───────────────────────────────
  // For customer view, always use the customer nav.
  // For staff view with multiple roles, merge all role navigations (deduplicated).
  //
  // `user.additionalModules` (set by the backend from
  // StaffRoleConfig.uiVisibility.additionalModules) ADDS nav items beyond
  // the hardcoded per-role list. Without this, an admin who granted
  // 'finance' to AFF Instructor via the UI Visibility tab would see
  // `hasPermission('finance') === true` but the sidebar would still hide
  // the Finance row because `getRoleNavigation('aff-instructor')` never
  // includes it. The map from a permission string back to the nav key is
  // derived from allNavItems so a future nav addition propagates
  // automatically.
  // permission → ALL nav keys carrying it, in allNavItems order. Several nav
  // items share one permission ('admin' → Admin + Booking Calendar + SMS config,
  // 'dashboard' → Dashboard + Analytics, 'manifest' → Manifest + Announcements),
  // so a first-key-wins map silently dropped every sibling on the
  // effectiveModules path: an admin-role user (whose backend list is the
  // ROLE_PERMISSIONS array, not owner's 'all') lost Booking Calendar, SMS
  // config, Analytics and Announcements from the sidebar entirely, while the
  // pages stayed reachable by URL. Expanding a granted module to all of its nav
  // items widens nothing — the permission is identical.
  //
  // `customerOnly` items (My Bookings) are excluded from the expansion: this map
  // feeds the STAFF lane, and 'bookings' there must resolve to the staff
  // schedule, never additionally surface My Bookings for dual-profile staff.
  const permissionToNavKeys = (() => {
    const m = new Map();
    for (const [key, item] of Object.entries(allNavItems)) {
      if (!item?.permission || item.customerOnly) continue;
      if (!m.has(item.permission)) m.set(item.permission, []);
      m.get(item.permission).push(key);
    }
    return m;
  })();

  // Build nav keys for a given view. Pure of React state — takes the
  // view explicitly so the handler that switches views can compute the
  // TARGET view's nav keys before the activeView state has flipped.
  const buildNavKeysFor = (view) => {
    if (view === 'customer') {
      return getRoleNavigation('customer');
    }
    const staffRoles = user?.staffRoles || [];

    // ── Override path: backend-computed effectiveModules wins ────────
    // When the backend returns `effectiveModules`, it ALREADY applied
    // per-role override semantics (Staff Config UI Visibility overrides
    // hardcoded defaults). We derive nav keys purely from that list —
    // no hardcoded role-nav merge, otherwise an admin who configured
    // "Pilot sees only [finance, safety, aircraft, staff]" would still
    // get the pilot's hardcoded ['dashboard', 'manifest', ...] items
    // leaking through.
    if (user?.effectiveModules === 'all') {
      return getRoleNavigation('admin'); // owner-equivalent
    }
    if (Array.isArray(user?.effectiveModules)) {
      const seen = new Set();
      const merged = [];
      const pushKeys = (mod) => {
        for (const key of permissionToNavKeys.get(mod) || []) {
          if (!seen.has(key)) { seen.add(key); merged.push(key); }
        }
      };
      // Always lead with 'dashboard' if granted, so the landing route
      // shows up at the top — purely a sort affordance.
      if (user.effectiveModules.includes('dashboard')) {
        pushKeys('dashboard');
      }
      for (const mod of user.effectiveModules) {
        pushKeys(mod);
      }
      // Profile last (parity with the legacy path).
      if (seen.has('profile')) {
        const idx = merged.indexOf('profile');
        if (idx !== merged.length - 1) {
          merged.splice(idx, 1);
          merged.push('profile');
        }
      }
      return merged;
    }

    // ── Legacy path: no effectiveModules on the user payload ────────
    // Old sessions / cached localStorage from before the override
    // rollout fall through here. Build from per-role hardcoded nav
    // and union additionalModules on top.
    const baseKeys = staffRoles.length === 0
      ? getRoleNavigation(user?.role || 'customer')
      : (() => {
          const seenSet = new Set();
          const out = [];
          for (const r of staffRoles) {
            for (const key of getRoleNavigation(r)) {
              if (!seenSet.has(key)) { seenSet.add(key); out.push(key); }
            }
          }
          return out;
        })();

    const seen = new Set(baseKeys);
    const merged = [...baseKeys];
    const additions = Array.isArray(user?.additionalModules) ? user.additionalModules : [];
    for (const mod of additions) {
      for (const key of permissionToNavKeys.get(mod) || []) {
        if (!seen.has(key)) { seen.add(key); merged.push(key); }
      }
    }

    if (seen.has('profile')) {
      const idx = merged.indexOf('profile');
      if (idx !== merged.length - 1) {
        merged.splice(idx, 1);
        merged.push('profile');
      }
    }
    return merged;
  };

  const navKeys = buildNavKeysFor(activeView);

  // Map keys to items and filter checks
  // ── Group host shape ──────────────────────────────────────────────────
  // On the group host the per-field Admin screen is dropped (it would
  // configure a dropzone nobody flies at) and the group console takes its
  // place — but ONLY for an owner. The console reads every field in the group
  // and can move people between them, which is above what a per-field admin
  // is trusted with, and the backend enforces exactly that with `requireOwner`.
  // Gating on `permission` alone would show it to admins and hand them a 403.
  const onGroupHost = isGroupHost();
  const isOwner = user?.role === 'owner' || (user?.staffRoles || []).includes('owner');
  // An owner on the group host IS the Super Admin, and the group host
  // administers no field of its own — every per-field screen there (Manifest,
  // Finance, Customers…) would show the `skydivingos` database, a dropzone
  // nobody flies at. So their sidebar is the Super Admin console and nothing
  // else; its Overview / Customers / Staff / Bookings / Transfers tabs are the
  // navigation. Non-owners keep the ordinary sidebar (the console 403s them).
  const superAdminOnly = onGroupHost && isOwner;

  const navigation = navKeys
    .map(key => allNavItems[key])
    .filter(item => item && hasPermission(item.permission))
    .filter(item => !superAdminOnly || item.href.startsWith('/app/group'))
    .filter(item => !(onGroupHost && item.href === '/app/admin'))
    .filter(item => !item.href.startsWith('/app/group') || (onGroupHost && isOwner))
    // `customerOnly` items (My Bookings) require a linked customer profile —
    // never surface them to non-customer accounts even if a staff role was
    // somehow granted the underlying permission (Bug 035). Gate on
    // customerProfile presence, not a role string.
    .filter(item => !item.customerOnly || !!user?.hasCustomerProfile);

  const isActive = (path) => location.pathname === path;

  /**
   * First accessible href for a given view. Computes the same nav-key
   * list the sidebar would render and returns the href of the first
   * non-profile item — Profile is always last and not a meaningful
   * landing page. Falls back to /app/profile if the user can only see
   * Profile, then to /app/dashboard as a last resort (legacy users with
   * no effectiveModules / no nav).
   */
  const firstHrefFor = (view) => {
    const keysForView = buildNavKeysFor(view);
    const items = keysForView
      .map((key) => allNavItems[key])
      .filter((item) => item && hasPermission(item.permission))
      // Same group-host shape as the sidebar, so "switch view" can never land
      // someone on a screen the sidebar deliberately hid.
      .filter((item) => !superAdminOnly || item.href.startsWith('/app/group'))
      .filter((item) => !(onGroupHost && item.href === '/app/admin'))
      .filter((item) => !item.href.startsWith('/app/group') || (onGroupHost && isOwner));
    // Prefer a non-profile landing — clicking "switch view" and landing
    // on Profile is bad UX. If the user has any other accessible nav
    // item, use its href.
    const nonProfile = items.find((item) => item.permission !== 'profile');
    if (nonProfile) return nonProfile.href;
    if (items.length > 0) return items[0].href;
    return '/app/dashboard';
  };

  // ─── View switching handler ─────────────────────────────────────────────
  const handleSwitchView = () => {
    const target = activeView === 'staff' ? 'customer' : 'staff';
    switchView(target);
    toast.success(target === 'customer' ? 'Switched to Jumper View 🪂' : 'Switched to Staff View 👨‍💼');
    // Navigate to the first screen the user is actually allowed to see
    // in the target view — not a hardcoded '/app/dashboard' that may
    // not even be in their effective modules under the new view.
    // Example: a Pilot whose Staff Config grants only [finance, safety,
    // aircraft, staff] lands on /app/finance after switching to staff
    // view, instead of getting a 403 trying to load /app/dashboard.
    navigate(firstHrefFor(target));
  };

  // Sidebar shared styles
  const sidebarStyle = { background: '#ffffff', borderRight: '1px solid #e5e7eb' };
  const navItemBase = 'flex items-center py-2.5 text-sm font-medium transition-all';
  const navItemActive = 'text-white';
  const navItemInactive = 'text-slate-600 hover:text-slate-900';
  const activeBarStyle = { background: '#C8372D' };

  return (
    <div className="h-screen flex overflow-hidden bg-white">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden md:flex md:flex-col transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}
        style={sidebarStyle}
      >
        {/* Logo and Toggle */}
        <div
          className={`flex ${collapsed ? 'flex-col items-center gap-3' : 'items-center justify-between px-6'} py-5`}
          style={{ borderBottom: '1px solid #e5e7eb' }}
        >
          {!collapsed && (
            <img
              src="/favicon.png"
              alt="Ultimate DZ Management"
              style={{
                height: '76px',
                objectFit: 'contain',
                filter: 'drop-shadow(0 10px 18px rgba(15, 23, 42, 0.12))',
              }}
            />
          )}
          {collapsed && (
            <div
              style={{
                height: '60px',
                width: '60px',
                borderRadius: '20px',
                background: 'linear-gradient(180deg, #ffffff 0%, #fff7ed 100%)',
                border: '1px solid #fdba74',
                boxShadow: '0 10px 22px rgba(200, 55, 45, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
              }}
            >
              <img
                src="/favicon.png"
                alt="Logo"
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 10px rgba(15, 23, 42, 0.12))',
                }}
              />
            </div>
          )}

          <Button
            variant="ghost"
            size="sm"
            className={collapsed ? 'h-8 w-8 p-0' : ''}
            style={{ color: '#64748b' }}
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Business Date Badge */}
        {!collapsed && (
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
            <div className="flex justify-between items-center mb-1">
              <div className="text-xs" style={{ color: '#64748b' }}>{t('common.businessDate')}</div>
              <div className="text-lg font-mono font-bold tracking-tight" style={{ color: '#C8372D' }}>
                {currentTime}
              </div>
            </div>
            <div className="text-sm font-semibold" style={{ color: '#0f172a' }}>{businessDate}</div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto custom-scrollbar">
          {navigation
            .filter(item => hasPermission(item.permission))
            .map((item) => {
            const active = isActive(item.href);

            return (
              <Link
                key={item.name}
                to={item.href}
                className={`${navItemBase} ${collapsed ? 'justify-center px-1' : 'justify-start px-3 gap-3'} ${active ? navItemActive : navItemInactive}`}
                style={active ? { ...activeBarStyle, color: '#ffffff' } : {}}
                title={collapsed ? item.name : ''}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', lineHeight: 'normal' }}>{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Language Switcher */}
        <div
          className={`px-4 py-2 ${collapsed ? 'flex justify-center' : ''}`}
          style={{ borderTop: '1px solid #e5e7eb' }}
        >
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && <span className="text-xs" style={{ color: '#64748b' }}>{t('common.language')}</span>}
            <LanguageSwitcher />
          </div>
        </div>

        {/* View Switch Button (desktop) */}
        {canSwitchView && !collapsed && (
          <div className="px-3 py-2" style={{ borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-8 text-xs gap-2"
              onClick={handleSwitchView}
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
              {activeView === 'staff' ? t('sidebar.switchToJumperView') : t('sidebar.switchToStaffView')}
            </Button>
          </div>
        )}
        {canSwitchView && collapsed && (
          <div className="px-2 py-2 flex justify-center" style={{ borderTop: '1px solid #e5e7eb' }}>
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleSwitchView}
              title={activeView === 'staff' ? t('sidebar.switchToJumperViewShort') : t('sidebar.switchToStaffViewShort')}
            >
              <ArrowLeftRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* User Menu */}
        <div className="p-4" style={{ borderTop: '1px solid #e5e7eb' }}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full ${collapsed ? 'justify-center px-0' : 'justify-start gap-3'} h-auto py-2`}
                style={{ color: '#0f172a' }}
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback style={{ background: activeView === 'customer' ? '#2563eb' : '#C8372D', color: '#ffffff' }}>
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium" style={{ color: '#0f172a' }}>{user?.name}</div>
                      <div className="text-xs capitalize" style={{ color: '#64748b' }}>
                        {activeView === 'customer' ? t('common.jumper') : (user?.staffRoles?.join(', ') || user?.role)}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4" style={{ color: '#64748b' }} />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={collapsed ? 'start' : 'end'} side={collapsed ? 'right' : 'bottom'} className="w-64">
              <DropdownMenuLabel>{t('common.myAccount')}</DropdownMenuLabel>
              {/* Show all staff roles as badges */}
              {activeView === 'staff' && user?.staffRoles?.length > 0 && (
                <>
                  <div className="px-2 pb-2">
                    <div className="text-xs text-muted-foreground mb-1.5">{t('common.roles')}</div>
                    <div className="flex flex-wrap gap-1">
                      {user.staffRoles.map((r) => (
                        <Badge key={r} variant="outline" className="text-[10px] capitalize">{r}</Badge>
                      ))}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              {activeView === 'customer' && (
                <>
                  <div className="px-2 pb-2">
                    {/* Shows the jumper type from the customer's most
                        recent confirmed booking. Only the current type
                        is shown — historical profileTypes are not. */}
                    <div className="text-xs text-muted-foreground mb-1.5">{t('common.jumperType')}</div>
                    <div className="flex flex-wrap gap-1">
                      {user?.currentType ? (
                        <Badge
                          className="bg-blue-100 text-blue-700 text-[10px] capitalize"
                          title={t('common.jumperTypeHint')}
                        >
                          {user.currentType.replace('-', ' ')}
                        </Badge>
                      ) : (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t('common.jumperProfile')}</Badge>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              {canSwitchDropzone && (
                <>
                  <div className="px-2 pb-2">
                    <div className="text-xs text-muted-foreground mb-1.5">{t('sidebar.dropzone')}</div>
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {activeDropzone?.name || t('sidebar.dropzoneUnknown')}
                    </div>
                  </div>
                  {DROPZONES.map((dz) => (
                    <DropdownMenuItem
                      key={dz.slug}
                      disabled={dz.slug === activeDropzone?.slug}
                      onClick={(e) => { e.preventDefault(); goToDropzone(dz.slug); }}
                    >
                      {dz.slug === activeDropzone?.slug
                        ? <Check className="mr-2 h-4 w-4" />
                        : hoppingTo === dz.slug
                          ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          : <span className="mr-2 h-4 w-4" />}
                      {dz.name}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              {canSwitchView && (
                <>
                  <DropdownMenuItem onClick={handleSwitchView}>
                    <ArrowLeftRight className="mr-2 h-4 w-4" />
                    {activeView === 'staff' ? t('sidebar.switchToJumperView') : t('sidebar.switchToStaffView')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 flex flex-col" style={sidebarStyle}>
            {/* Logo */}
            <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <img
                src="/favicon.png"
                alt="Ultimate DZ Management"
                style={{
                  height: '56px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 18px rgba(15, 23, 42, 0.12))',
                }}
              />
              <Button variant="ghost" size="sm" style={{ color: '#64748b' }} onClick={() => setSidebarOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Business Date */}
            <div className="px-4 py-3" style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
              <div className="flex justify-between items-center mb-1">
                <div className="text-xs" style={{ color: '#64748b' }}>{t('common.businessDate')}</div>
                <div className="text-lg font-mono font-bold tracking-tight" style={{ color: '#C8372D' }}>
                  {currentTime}
                </div>
              </div>
              <div className="text-sm font-semibold" style={{ color: '#0f172a' }}>{businessDate}</div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
              {navigation
                .filter(item => hasPermission(item.permission))
                .map((item) => {
                const active = isActive(item.href);

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${navItemBase} justify-start px-3 gap-3 ${active ? navItemActive : navItemInactive}`}
                    style={active ? { ...activeBarStyle, color: '#ffffff' } : {}}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, textTransform: 'none', letterSpacing: 'normal', lineHeight: 'normal' }}>{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Language Switcher */}
            <div className="px-4 py-2" style={{ borderTop: '1px solid #e5e7eb' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: '#64748b' }}>{t('common.language')}</span>
                <LanguageSwitcher />
              </div>
            </div>

            {/* User Section */}
            <div className="p-4" style={{ borderTop: '1px solid #e5e7eb' }}>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9">
                  <AvatarFallback style={{ background: activeView === 'customer' ? '#2563eb' : '#C8372D', color: '#ffffff' }}>
                    {user?.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="text-sm font-medium" style={{ color: '#0f172a' }}>{user?.name}</div>
                  <div className="text-xs capitalize" style={{ color: '#64748b' }}>
                    {activeView === 'customer' ? t('common.jumper') : (user?.staffRoles?.join(', ') || user?.role)}
                  </div>
                </div>
              </div>
              {/* All staff roles */}
              {activeView === 'staff' && user?.staffRoles?.length > 1 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {user.staffRoles.map((r) => (
                    <Badge key={r} variant="outline" className="text-[10px] capitalize">{r}</Badge>
                  ))}
                </div>
              )}
              {canSwitchView && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mb-2 h-8 text-xs gap-2"
                  onClick={() => { handleSwitchView(); setSidebarOpen(false); }}
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  {activeView === 'staff' ? t('sidebar.switchToJumperView') : t('sidebar.switchToStaffView')}
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full"
                style={{ borderColor: '#d1d5db', color: '#0f172a', background: 'transparent' }}
                onClick={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('common.logout')}
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar - Mobile */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-border bg-white">
          <Button variant="ghost" size="sm" style={{ color: '#0f172a' }} onClick={() => setSidebarOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <img src="/favicon.png" alt="Ultimate DZ Management" style={{ height: '36px', objectFit: 'contain' }} />
          <div className="w-10" />
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
